import { ChainId } from '@wowswap-io/wowswap-sdk'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ReactComponent as IconAvalanche } from 'assets/landing-images/avalanche.svg'
import { ReactComponent as IconBSC } from 'assets/landing-images/bsc.svg'
import { ReactComponent as IconETH } from 'assets/landing-images/eth.svg'
import { ReactComponent as IconPolygon } from 'assets/landing-images/polygon.svg'

// import { ReactComponent as IconSolana } from 'assets/landing-images/solana.svg'
import * as S from './styleds'

const chains = [
  { name: 'BNB', id: ChainId.MAINNET, Icon: IconBSC, nativeToken: 'BNB', importantTokens: ['BUSD'] },
  { name: 'Polygon', id: ChainId.MATIC, Icon: IconPolygon, nativeToken: 'MATIC', importantTokens: ['USDC', 'ETH'] },
  { name: 'Avalanche', id: ChainId.AVALANCE, Icon: IconAvalanche, nativeToken: 'AVAX', importantTokens: ['USDT'] },
  { name: 'ETH', id: ChainId.ETHEREUM, Icon: IconETH, nativeToken: 'ETH', importantTokens: ['USDC'] }
  // { name: 'Solana', Icon: IconSolana }
] as const

const MAX_VISIBLE_TOKENS = 6

type Chain = typeof chains[number]

type ChainName = Chain['name']

type Token = {
  name: string
  APY: number
  TVL?: number
}

type TokensByChains = Partial<Record<ChainName, Token[]>>

const convertTokenName = (name: string) => {
  const nativeName = chains.find(chain => `W${chain.nativeToken}` === name)?.nativeToken
  if (nativeName) return nativeName
  if (name === 'USDT.E') return 'USDT'
  return name
}

const formatTVL = (TVL: number) =>
  // @ts-ignore
  Intl.NumberFormat('en', { notation: 'compact', style: 'currency', currency: 'USD' }).format(TVL)

const addMostImportantTokens = (chain: Chain, bestTokens: Token[], allTokens: Token[]) => {
  const importantTokensNames: string[] = [...chain.importantTokens, chain.nativeToken]
  const tokensToAdd = importantTokensNames
    .map(importantTokenName => allTokens.find(token => token.name === importantTokenName) || null)
    .filter((tokenToAdd): tokenToAdd is Token => tokenToAdd !== null)
    .filter(tokenToAdd => !bestTokens.find(token => token.name === tokenToAdd.name))

  for (let i = MAX_VISIBLE_TOKENS - 1; i >= 0; i--) {
    if (tokensToAdd.length === 0) break
    if (importantTokensNames.includes(bestTokens[i].name)) continue

    bestTokens[i] = tokensToAdd.pop() as Token
  }

  bestTokens.sort((a, b) => b.APY - a.APY)
}

export const CoinsPanel = () => {
  const [chain, setChain] = useState<ChainName>('BNB')
  const [tokens, setTokens] = useState<TokensByChains>({})
  const { t } = useTranslation()

  const chainId = chains.find(el => el.name === chain)?.id

  useEffect(() => {
    const fetchTokens = async () => {
      const tokens: TokensByChains = {}
      let APYdata: Record<string, Record<string, string>> = {}
      let TVLdata: Record<string, Record<string, string>> = {}

      try {
        const [responseAPY, responseTVL] = await Promise.allSettled([
          fetch(`${process.env.REACT_APP_CHARTS_URL}/token/apy`),
          fetch(`${process.env.REACT_APP_CHARTS_URL}/token/tvl`)
        ])

        if (responseAPY.status === 'rejected') return
        APYdata = await responseAPY.value.json()

        if (responseTVL.status === 'fulfilled') {
          TVLdata = await responseTVL.value.json()
        }

        chains.forEach(chain => {
          const allTokens = Object.entries(APYdata[chain.id]).map(([token, APY]) => ({
            name: convertTokenName(token),
            APY: Number(APY),
            TVL: Number(TVLdata[chain.id]?.[token])
          }))

          const bestTokens = allTokens.sort((a, b) => b.APY - a.APY).slice(0, MAX_VISIBLE_TOKENS)
          addMostImportantTokens(chain, bestTokens, allTokens)

          tokens[chain.name] = bestTokens
        })
      } catch (error) {
        console.error(error)
        return
      }

      setTokens(tokens)
    }

    fetchTokens()
  }, [chain])

  return (
    <S.Panel>
      <S.Title>{t('Most profitable coins on WOWswap')}</S.Title>
      <S.Coins>
        {(tokens[chain] || []).map(({ name, APY, TVL }) => (
          <S.Coin key={name} to={`/earn?chain=${chainId}`}>
            <S.CoinIcon
              src={`https://raw.githubusercontent.com/wowswap-io/assets/master/${name}.png`}
              alt={`Coin ${name}`}
            />
            <S.CoinName>{name}</S.CoinName>
            <S.CoinParameter data-coin-parameter="APY">
              <S.CoinParameterName>{t('APY')}: </S.CoinParameterName>
              {APY}%
            </S.CoinParameter>
            <S.CoinParameter data-coin-parameter="TVL">
              <S.CoinParameterName>{t('TVL')}: </S.CoinParameterName>
              {TVL ? formatTVL(TVL) : '. . .'}
            </S.CoinParameter>
          </S.Coin>
        ))}
      </S.Coins>
      <S.Chains>
        {chains.map(({ name, Icon }) => (
          <S.Chain key={name} active={name === chain} onClick={() => setChain(name)}>
            <Icon />
            <span>{name}</span>
          </S.Chain>
        ))}
      </S.Chains>
    </S.Panel>
  )
}
